/**
 * Log levels matching console methods
 */
export enum LogLevel {
  ERROR = 'error',
  WARN = 'warn',
  INFO = 'info',
  DEBUG = 'debug',
  TRACE = 'trace'
}

/**
 * Logger configuration options
 */
export interface LoggerOptions {
  /**
   * Namespace for the logger (e.g., 'six3ro:auth', 'six3ro:p2p:helia')
   */
  namespace: string;

  /**
   * Minimum log level to display
   * @default LogLevel.INFO
   */
  level?: LogLevel;

  /**
   * Enable/disable logging for this namespace
   * @default true
   */
  enabled?: boolean;

  /**
   * Custom prefix color (hex or named CSS color)
   */
  color?: string;
}

/**
 * Logger interface for namespaced logging
 */
export interface Logger {
  /**
   * Log an error message
   */
  error: (...args: any[]) => void;

  /**
   * Log a warning message
   */
  warn: (...args: any[]) => void;

  /**
   * Log an info message
   */
  info: (...args: any[]) => void;

  /**
   * Log a debug message
   */
  debug: (...args: any[]) => void;

  /**
   * Log a trace message (most verbose)
   */
  trace: (...args: any[]) => void;

  /**
   * Enable logging for this logger instance
   */
  enable: () => void;

  /**
   * Disable logging for this logger instance
   */
  disable: () => void;

  /**
   * Check if this logger is enabled
   */
  isEnabled: () => boolean;

  /**
   * Get the namespace for this logger
   */
  getNamespace: () => string;
}

/**
 * Global logger configuration
 */
export interface LoggerConfig {
  /**
   * Global minimum log level
   * @default LogLevel.INFO
   */
  level?: LogLevel;

  /**
   * Pattern to enable specific namespaces (e.g., 'six3ro:*', 'six3ro:p2p:*')
   * Similar to DEBUG environment variable pattern
   */
  enabled?: string;

  /**
   * Timestamp format function
   */
  formatTimestamp?: () => string;

  /**
   * Whether to include timestamps
   * @default true
   */
  includeTimestamp?: boolean;
}
